import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { 
  ShieldCheck, Zap, IndianRupee, Star, 
  Search, Calendar, CreditCard, Sparkles, 
  User, MapPin, ChevronRight, Building2, 
  Globe, Scissors, Heart, Palette, 
  Sun, Smile, MessageCircle, Building
} from "lucide-react";
import { useSalons, useCategories } from "../api/hooks";
import { useAuth } from "../App";
import { useTranslation } from "../i18n";
import Bokeh from "../components/Bokeh";
import MarketingShell from "../components/marketing/MarketingShell";
import {
  SectionTitle,
  Section,
  Stat,
  FeatureCard,
  StepCard,
  Testimonial,
  CalloutBanner,
} from "../components/marketing/MarketingBlocks";
import {
  Stars,
  BtnGold,
  BtnGhost,
  Badge,
  Spinner,
} from "../components/UI";
import { T } from "../tokens";

const CITIES = [
  "All Cities", "Chennai", "Mumbai", "Bangalore", "Delhi", "Hyderabad",
  "Pune", "Kolkata", "Gurgaon",
];

const MARQUEE_ITEMS = [
  "✦ Bridal Packages", "✦ Hair Colour", "✦ Balayage", "✦ Keratin Treatment",
  "✦ Skin Treatments", "✦ Nail Art", "✦ Beard Grooming", "✦ Anti-Ageing Facials",
  "✦ Hair Extensions", "✦ Waxing", "✦ Eyebrow Shaping", "✦ Hair Spa",
];

const WHY_ITEMS = [
  { icon: <ShieldCheck size={24} />, title: "Verified Premium Salons", desc: "Every partner salon is hand-inspected and certified by our quality team." },
  { icon: <Zap size={24} />,         title: "Instant Confirmation",     desc: "Real-time slot availability. Book in under 60 seconds." },
  { icon: <IndianRupee size={24} />, title: "Best Price Guarantee",     desc: "No hidden charges. The price you see is the price you pay." },
  { icon: <Star size={24} />,        title: "Loyalty That Pays Back",   desc: "Earn points on every visit and redeem for free services." },
];

const HOW_STEPS = [
  { step: 1, icon: <Search size={24} />,      title: "Discover",   desc: "Browse 500+ premium salons filtered by city, service and vibe." },
  { step: 2, icon: <Calendar size={24} />,    title: "Book",       desc: "Pick your slot, stylist and services — confirmed in seconds." },
  { step: 3, icon: <CreditCard size={24} />,  title: "Pay Easily", desc: "Pay online via UPI / card / wallet or at the salon." },
  { step: 4, icon: <Sparkles size={24} />,    title: "Enjoy",       desc: "Show up and unwind. Earn points and leave a review." },
];

const TESTIMONIALS = [
  {
    quote: "Fayriza is the only booking app that actually feels premium. The stylists at the salons they verify are genuinely excellent — my bridal trial was flawless.",
    name: "Ananya R.", role: "Bride · Chennai", avatar: <User size={20} />, rating: 5,
  },
  {
    quote: "I've been using the Gold membership for a year. Saved more than ₹12,000 and got priority slots every festive season. Worth every rupee.",
    name: "Meera K.", role: "Gold Member · Mumbai", avatar: <User size={20} />, rating: 5,
  },
  {
    quote: "The AI Beauty Advisor recommended a stylist who completely nailed my hair colour. I wouldn't have found her otherwise.",
    name: "Shreya P.", role: "Bangalore", avatar: <User size={20} />, rating: 5,
  },
];

const PRESS = [
  "YourStory", "VogueIndia", "Elle", "Femina", "Economic Times", "Mint",
];

// Map backend icon strings to Lucide components
const CATEGORY_ICON = {
  ring: Sparkles, bridal: Sparkles,
  scissors: Scissors, hair: Scissors,
  face: Smile, skin: Smile,
  nail: Palette, nails: Palette,
  spa: Sun,
  razor: User, grooming: User, beard: User,
  massage: Heart, wax: Heart, makeup: Palette,
};

const categoryIcon = (cat) => {
  const IconComp = CATEGORY_ICON[cat?.icon] || CATEGORY_ICON[cat?.slug] || Sparkles;
  return <IconComp size={30} color={T.gold} />;
};

function SalonCard({ salon }) {
  const navigate = useNavigate();
  const [hov, setHov] = useState(false);
  const cover = salon.images?.find((i) => i.is_cover)?.url;
  return (
    <div
      role="button"
      tabIndex={0}
      onMouseOver={() => setHov(true)}
      onMouseOut={() => setHov(false)}
      onClick={() => navigate(`/salons/${salon.slug}`)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") navigate(`/salons/${salon.slug}`);
      }}
      style={{
        background: T.dark3,
        border: `1px solid ${hov ? "rgba(201,168,76,.5)" : T.goldBdr}`,
        borderRadius: 16,
        overflow: "hidden",
        cursor: "pointer",
        transition: "all .28s",
        transform: hov ? "translateY(-6px)" : "none",
        boxShadow: hov ? "0 16px 48px rgba(0,0,0,.5)" : "none",
        flexShrink: 0,
        width: 280,
      }}
    >
      <div
        style={{
          height: 170,
          background: cover ? `url(${cover}) center/cover` : "linear-gradient(160deg,#3D2B1A,#1A1008)",
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        {!cover && <Scissors size={52} color={T.goldLt} style={{ opacity: 0.22 }} />}
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(0deg,rgba(13,11,8,.8) 0%,transparent 50%)" }} />
        <div style={{ position: "absolute", top: 10, right: 10, background: "rgba(13,11,8,.8)", backdropFilter: "blur(6px)", border: `1px solid ${T.goldBdrS}`, padding: "4px 10px", borderRadius: 20, display: "flex", alignItems: "center", gap: 4 }}>
          <Star size={10} color={T.gold} fill={T.gold} />
          <span style={{ fontSize: 12, fontWeight: 600, color: T.goldLt }}>{salon.rating}</span>
        </div>
        {salon.is_featured && (
          <div style={{ position: "absolute", top: 10, left: 10 }}>
            <Badge variant="gold">Featured</Badge>
          </div>
        )}
      </div>
      <div style={{ padding: "14px 16px" }}>
        <div style={{ fontFamily: "Cormorant Garamond,serif", fontSize: 18, fontWeight: 600, color: T.text, marginBottom: 3 }}>
          {salon.name}
        </div>
        <div style={{ fontSize: 12, color: T.muted, marginBottom: 8, display: "flex", alignItems: "center", gap: 4 }}>
          <MapPin size={12} color={T.gold} /> {salon.area}, {salon.city}
        </div>
        <Stars rating={salon.rating} size={11} reviewCount={salon.review_count} />
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 16px 14px", borderTop: `1px solid ${T.goldBdr}` }}>
        <span style={{ fontSize: 12, color: T.muted }}>
          From{" "}
          <span style={{ color: T.goldLt, fontWeight: 500 }}>
            {salon.min_price ? `₹${Number(salon.min_price).toLocaleString("en-IN")}` : "See prices"}
          </span>
        </span>
        <span style={{ fontSize: 12.5, color: T.gold, display: "flex", alignItems: "center", gap: 4 }}>Book <ChevronRight size={14} /></span>
      </div>
    </div>
  );
}

/* ── Animated count-up tile ─────────────────────────────────── */
function CountUp({ value, suffix = "", duration = 1800 }) {
  const [n, setN] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const io = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || started.current) return;
        started.current = true;
        const start = performance.now();
        const tick = (t) => {
          const p = Math.min((t - start) / duration, 1);
          setN(Math.floor(p * value));
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      },
      { threshold: 0.3 }
    );
    if (ref.current) io.observe(ref.current);
    return () => io.disconnect();
  }, [value, duration]);

  return (
    <span ref={ref}>
      {n.toLocaleString("en-IN")}
      {suffix}
    </span>
  );
}

export default function HomePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [city, setCity] = useState("All Cities");

  useEffect(() => {
    if (user) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  const { data: salonsData, isLoading } = useSalons({
    ...(city && city !== "All Cities" && { city }),
    is_featured: true,
    per_page: 8,
  });
  const { data: categoriesResp } = useCategories();
  const salons = salonsData?.data?.data || [];
  // `useCategories` returns the full axios response; unwrap to the
  // array the backend puts under response.data.data (flat list, no
  // pagination on this endpoint).
  const categoriesData = categoriesResp?.data?.data ?? categoriesResp?.data ?? null;
  const categories = Array.isArray(categoriesData) && categoriesData.length
    ? categoriesData
    : null;

  const handleSearch = () =>
    navigate(`/salons?q=${encodeURIComponent(query)}&city=${encodeURIComponent(city)}`);

  return (
    <MarketingShell
      title="Fayriza — India's Premium Salon Booking Platform"
      description="Book luxury beauty, hair and grooming services at 500+ verified premium salons across India. Instant confirmation, best-price guarantee, loyalty rewards."
    >
      {/* ── HERO ───────────────────────────────────────────────── */}
      <section
        style={{
          position: "relative",
          minHeight: "calc(100vh - 88px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          background:
            "radial-gradient(ellipse at 55% 0%,#2A1F08 0%,#0D0B08 65%)",
        }}
      >
        <Bokeh count={28} />
        <div
          style={{
            position: "relative",
            zIndex: 1,
            textAlign: "center",
            padding: "0 24px",
            maxWidth: 820,
            margin: "0 auto",
          }}
        >
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              fontSize: 11,
              color: T.gold,
              letterSpacing: ".22em",
              textTransform: "uppercase",
              marginBottom: 18,
              padding: "8px 16px",
              background: T.goldDim,
              border: `1px solid ${T.goldBdrS}`,
              borderRadius: 20,
              animation: "fyzFadeUp .6s ease",
            }}
          >
            <Sparkles size={12} /> {t("home.hero_eyebrow")}
          </div>
          <h1
            style={{
              fontFamily: "Cormorant Garamond,serif",
              fontSize: "clamp(40px,6vw,80px)",
              fontWeight: 300,
              color: T.text,
              lineHeight: 1.03,
              marginBottom: 22,
              animation: "fyzFadeUp .7s ease",
            }}
          >
            <em style={{ color: T.goldLt, fontWeight: 600, fontStyle: "italic" }}>
              {t("home.hero_title")}
            </em>
          </h1>
          <p
            style={{
              fontSize: 16,
              color: T.muted,
              lineHeight: 1.85,
              maxWidth: 560,
              margin: "0 auto 36px",
              animation: "fyzFadeUp .8s ease",
            }}
          >
            {t("home.hero_subtitle")}
          </p>
          {/* Search bar */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSearch();
            }}
            style={{
              display: "flex",
              alignItems: "stretch",
              background: "rgba(26,23,16,.95)",
              border: `1.5px solid ${T.goldBdrS}`,
              borderRadius: 14,
              overflow: "hidden",
              boxShadow: "0 20px 60px rgba(0,0,0,.5)",
              maxWidth: 680,
              margin: "0 auto 28px",
              animation: "fyzFadeUp .9s ease",
              height: 64
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                flex: 1.5,
                padding: "0 24px",
                borderRight: `1px solid ${T.goldBdr}`,
              }}
            >
              <Search size={18} color={T.gold} style={{ opacity: 0.6 }} />
              <input
                aria-label={t("common.search")}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("home.search_placeholder")}
                style={{
                  flex: 1,
                  background: "transparent",
                  border: "none",
                  outline: "none",
                  color: T.text,
                  fontSize: 14.5,
                  fontFamily: "inherit",
                }}
              />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1, padding: "0 24px" }}>
              <MapPin size={18} color={T.gold} style={{ opacity: 0.6 }} />
              <select
                aria-label="Choose a city"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                style={{
                  flex: 1,
                  background: "transparent",
                  border: "none",
                  outline: "none",
                  color: T.text,
                  fontSize: 13.5,
                  fontFamily: "inherit",
                  cursor: "pointer",
                }}
              >
                {CITIES.map((c) => (
                  <option key={c} style={{ background: T.dark3 }}>{c}</option>
                ))}
              </select>
            </div>
            <button
              type="submit"
              style={{
                padding: "0 32px",
                flexShrink: 0,
                background: `linear-gradient(135deg,${T.goldLt},${T.gold})`,
                border: "none",
                color: T.dark,
                fontSize: 15,
                fontWeight: 700,
                cursor: "pointer",
                fontFamily: "inherit",
                transition: "all .2s"
              }}
              onMouseOver={e => e.target.style.opacity = .9}
              onMouseOut={e => e.target.style.opacity = 1}
            >
              {t("home.search_cta")}
            </button>
          </form>
          {/* Trust row */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: 28,
              flexWrap: "wrap",
              animation: "fyzFadeUp 1s ease",
            }}
          >
            {["500+ Verified Salons", "25+ Cities", "Instant Booking", "4.8★ Avg Rating"].map((s) => (
              <div
                key={s}
                style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: T.muted }}
              >
                <Sparkles size={10} color={T.gold} />
                {s}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── MARQUEE ────────────────────────────────────────────── */}
      <div
        style={{
          background: T.dark3,
          borderTop: `1px solid ${T.goldBdr}`,
          borderBottom: `1px solid ${T.goldBdr}`,
          overflow: "hidden",
          padding: "16px 0",
        }}
        aria-hidden="true"
      >
        <div
          style={{
            display: "flex",
            gap: 40,
            animation: "fyzScrollX 35s linear infinite",
            width: "max-content",
          }}
        >
          {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((item, i) => (
            <span key={i} style={{ fontSize: 12.5, color: T.muted, whiteSpace: "nowrap", letterSpacing: ".1em", display: "flex", alignItems: "center", gap: 8 }}>
              <Sparkles size={12} color={T.goldBdr} /> {item.replace("✦ ", "")}
            </span>
          ))}
        </div>
        <style>{`@keyframes fyzScrollX{0%{transform:translateX(0)}100%{transform:translateX(-50%)}}`}</style>
      </div>

      {/* ── LIVE STATS ─────────────────────────────────────────── */}
      <Section padding="64px 48px">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: 24,
          }}
        >
          <Stat icon={<Building2 size={24} />}  value={<CountUp value={540} suffix="+" />} label="Verified Salons" />
          <Stat icon={<Globe size={24} />}      value={<CountUp value={25}  suffix=" cities" />} label="India Coverage" />
          <Stat icon={<Scissors size={24} />}   value={<CountUp value={240000} suffix="+" />} label="Bookings Served" />
          <Stat icon={<Star size={24} />}       value="4.8 / 5" label="Avg Customer Rating" />
        </div>
      </Section>

      {/* ── CATEGORIES ─────────────────────────────────────────── */}
      <Section padding="16px 48px 64px">
        <SectionTitle
          eyebrow={t("home.categories_title")}
          title={t("home.categories_sub")}
        />
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
          {(categories || [
            { id: 1, name: "Bridal",   slug: "bridal",   icon: "bridal" },
            { id: 2, name: "Hair",     slug: "hair",     icon: "hair" },
            { id: 3, name: "Skin",     slug: "skin",     icon: "skin" },
            { id: 4, name: "Nails",    slug: "nails",    icon: "nails" },
            { id: 5, name: "Spa",      slug: "spa",      icon: "spa" },
            { id: 6, name: "Grooming", slug: "grooming", icon: "grooming" },
          ]).map((cat) => (
            <button
              type="button"
              key={cat.id}
              onClick={() => navigate(`/salons?cat=${cat.slug}`)}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 12,
                padding: "24px 28px",
                background: T.dark3,
                border: `1px solid ${T.goldBdr}`,
                borderRadius: 16,
                cursor: "pointer",
                transition: "all .25s",
                minWidth: 120,
                color: T.text,
                fontFamily: "inherit",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = T.goldDim;
                e.currentTarget.style.borderColor = T.gold;
                e.currentTarget.style.transform = "translateY(-4px)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = T.dark3;
                e.currentTarget.style.borderColor = T.goldBdr;
                e.currentTarget.style.transform = "";
              }}
            >
              <div aria-hidden="true">{categoryIcon(cat)}</div>
              <span style={{ fontSize: 13, fontWeight: 600, letterSpacing: "0.02em" }}>{cat.name}</span>
            </button>
          ))}
        </div>
      </Section>

      {/* ── FEATURED SALONS ────────────────────────────────────── */}
      <Section padding="16px 48px 64px">
        <div
          style={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "space-between",
            marginBottom: 28,
            flexWrap: "wrap",
            gap: 12,
          }}
        >
          <SectionTitle
            eyebrow={t("home.featured_salons")}
            title={`${t("home.featured_salons")} · ${city}`}
          />
          <button
            type="button"
            onClick={() => navigate(`/salons?city=${city}`)}
            style={{
              background: "transparent",
              border: "none",
              color: T.gold,
              fontSize: 14,
              fontWeight: 500,
              cursor: "pointer",
              fontFamily: "inherit",
              paddingBottom: 8,
              display: "flex",
              alignItems: "center",
              gap: 6
            }}
          >
            {t("common.view_all")} <ChevronRight size={16} />
          </button>
        </div>
        {isLoading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "60px 0" }}>
            <Spinner size={42} />
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              gap: 20,
              overflowX: "auto",
              paddingBottom: 20,
              scrollbarWidth: "none",
            }}
          >
            {salons.length
              ? salons.map((s) => <SalonCard key={s.id} salon={s} />)
              : Array.from({ length: 4 }).map((_, i) => (
                  <div
                    key={i}
                    style={{
                      width: 280,
                      flexShrink: 0,
                      height: 290,
                      background: T.dark3,
                      border: `1px solid ${T.goldBdr}`,
                      borderRadius: 16,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Spinner size={24} />
                  </div>
                ))}
          </div>
        )}
      </Section>

      {/* ── HOW IT WORKS ───────────────────────────────────────── */}
      <Section background={T.dark2} padding="72px 48px">
        <SectionTitle
          eyebrow="How It Works"
          title="Four steps to your next glow-up"
          description="Fayriza is designed to feel effortless. No tedious calls. No guesswork on pricing. Just choose, book, and be pampered."
          center
        />
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: 24,
            marginTop: 36,
          }}
        >
          {HOW_STEPS.map((s) => (
            <StepCard
              key={s.step}
              step={s.step}
              icon={s.icon}
              title={s.title}
              description={s.desc}
            />
          ))}
        </div>
        <div style={{ textAlign: "center", marginTop: 40 }}>
          <BtnGhost onClick={() => navigate("/how-it-works")} style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            See the full journey <ChevronRight size={18} />
          </BtnGhost>
        </div>
      </Section>

      {/* ── WHY FAYRIZA ────────────────────────────────────────── */}
      <Section padding="72px 48px">
        <SectionTitle
          eyebrow="Why Choose Us"
          title="The Fayriza difference"
          center
        />
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
            gap: 24,
          }}
        >
          {WHY_ITEMS.map((w) => (
            <FeatureCard
              key={w.title}
              icon={w.icon}
              title={w.title}
              description={w.desc}
            />
          ))}
        </div>
      </Section>

      {/* ── TESTIMONIALS ───────────────────────────────────────── */}
      <Section background={T.dark2} padding="72px 48px">
        <SectionTitle
          eyebrow="Loved by 200K+ women & men"
          title="What our customers say"
          center
        />
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: 24,
          }}
        >
          {TESTIMONIALS.map((t) => (
            <Testimonial key={t.name} {...t} />
          ))}
        </div>
      </Section>

      {/* ── AI BANNER ──────────────────────────────────────────── */}
      <Section padding="24px 48px 64px">
        <CalloutBanner
          eyebrow="Fayriza AI"
          title={
            <>
              Let AI find your{" "}
              <em style={{ color: T.goldLt, fontWeight: 600, fontStyle: "italic" }}>
                perfect salon
              </em>
            </>
          }
          description="Answer five quick questions and get personalised recommendations for salons, services and products matched to your unique beauty profile."
          icon={<Sparkles size={32} color={T.goldLt} />}
          actions={
            <>
              <BtnGold size="lg" onClick={() => navigate("/ai")} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                Try AI Beauty Advisor <ChevronRight size={20} />
              </BtnGold>
              <BtnGhost size="lg" onClick={() => navigate("/how-it-works")}>
                How it works
              </BtnGhost>
            </>
          }
        />
      </Section>

      {/* ── PARTNER / STYLIST CTA ROW ─────────────────────────── */}
      <Section padding="16px 48px 72px">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
            gap: 24,
          }}
        >
          <CalloutBanner
            eyebrow="For Salons"
            title={
              <>
                Grow your salon with{" "}
                <em style={{ color: T.goldLt, fontWeight: 600, fontStyle: "italic" }}>
                  Fayriza
                </em>
              </>
            }
            description="Reach new customers, fill empty slots and manage everything from a single dashboard."
            icon={<Building2 size={32} color={T.goldLt} />}
            actions={
              <BtnGold onClick={() => navigate("/partners")} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                Become a Partner <ChevronRight size={18} />
              </BtnGold>
            }
          />
          <CalloutBanner
            eyebrow="For Stylists"
            title={
              <>
                Build your{" "}
                <em style={{ color: T.goldLt, fontWeight: 600, fontStyle: "italic" }}>
                  personal brand
                </em>
              </>
            }
            description="Showcase your portfolio, take direct bookings and earn more with Fayriza Stylist."
            icon={<Scissors size={32} color={T.goldLt} />}
            actions={
              <BtnGold onClick={() => navigate("/stylists")} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                Join as a Stylist <ChevronRight size={18} />
              </BtnGold>
            }
          />
        </div>
      </Section>

      {/* ── MEMBERSHIP CTA ─────────────────────────────────────── */}
      <Section padding="16px 48px 64px">
        <CalloutBanner
          eyebrow="Fayriza Gold Membership"
          title={
            <>
              Save <span style={{ color: T.goldLt, fontWeight: 600 }}>20%</span> on every salon visit
            </>
          }
          description="Join 50,000+ Gold members enjoying priority booking, exclusive discounts and loyalty multipliers — from ₹999 / year."
          icon={<ShieldCheck size={32} color={T.goldLt} />}
          actions={
            <>
              <BtnGold size="lg" onClick={() => navigate("/membership")} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                Get Gold Membership <ChevronRight size={20} />
              </BtnGold>
              <BtnGhost size="lg" onClick={() => navigate("/membership")}>
                Compare Plans
              </BtnGhost>
            </>
          }
        />
      </Section>

      {/* ── PRESS ──────────────────────────────────────────────── */}
      <Section padding="32px 48px 96px">
        <div
          style={{
            textAlign: "center",
            fontSize: 11,
            color: T.gold,
            letterSpacing: ".22em",
            textTransform: "uppercase",
            marginBottom: 24,
          }}
        >
          As Featured In
        </div>
        <div
          style={{
            display: "flex",
            gap: 48,
            justifyContent: "center",
            flexWrap: "wrap",
            opacity: 0.5,
          }}
        >
          {PRESS.map((p) => (
            <span
              key={p}
              style={{
                fontFamily: "Cormorant Garamond,serif",
                fontSize: 22,
                color: T.muted,
                fontStyle: "italic",
                letterSpacing: "0.02em"
              }}
            >
              {p}
            </span>
          ))}
        </div>
      </Section>

      {/* Hint to existing users they can skip straight to booking */}
      {!user && (
        <div style={{ display: "none" }} aria-hidden="true">
          {/* kept for SEO crawlers discovering account pages */}
          <a href="/login">Login</a>
          <a href="/register">Register</a>
        </div>
      )}
    </MarketingShell>
  );
}

